import { z } from 'zod'

import { getAssetId } from './cms.link'

export const richTextMarkSchema = z.object({
    type: z.string(),
    attrs: z.record(z.string(), z.unknown()).optional(),
})

export type RichTextMark = z.infer<typeof richTextMarkSchema>

export type RichTextNode = {
    type: string
    attrs?: Record<string, unknown>
    content?: RichTextNode[]
    marks?: RichTextMark[]
    text?: string
}

export const richTextNodeSchema: z.ZodType<RichTextNode> = z.lazy(() =>
    z.object({
        type: z.string(),
        attrs: z.record(z.string(), z.unknown()).optional(),
        content: z.array(richTextNodeSchema).optional(),
        marks: z.array(richTextMarkSchema).optional(),
        text: z.string().optional(),
    }),
)

/**
 * The public document type is deliberately shallow: `content` is `unknown[]`, not
 * `RichTextNode[]`.
 *
 * This is a compiler limit, not taste. A rich-text doc sits inside the page form's block union,
 * and react-hook-form's `FieldPath` recurses into every value type to build its dotted key union.
 * A self-referential `content` makes that recursion blow up with "Type instantiation is
 * excessively deep and possibly infinite". The shallow variant compiles clean while validation
 * stays deep via `richTextNodeSchema`.
 */
export type RichTextDoc = {
    type: 'doc'
    content?: unknown[]
}

export const richTextDocSchema: z.ZodType<RichTextDoc, unknown> = z.object({
    type: z.literal('doc'),
    content: z.array(richTextNodeSchema).optional(),
})

/** The single seam where the shallow-type cast lives. Every tree walk starts here. */
export const richTextNodes = (doc: RichTextDoc | null | undefined): RichTextNode[] =>
    (doc?.content ?? []) as RichTextNode[]

/**
 * One empty paragraph — the shape Tiptap produces for an empty editor. A factory, not a constant:
 * block defaults must not share one instance.
 */
export const emptyRichTextDoc = (): RichTextDoc => ({
    type: 'doc',
    content: [{ type: 'paragraph' }],
})

const hasVisibleContent = (node: RichTextNode): boolean => {
    if (node.type === 'text') return (node.text ?? '').trim() !== ''
    if (node.type === 'hardBreak') return false
    if (node.content && node.content.length > 0) return node.content.some(hasVisibleContent)
    // A leaf that is not text carries its own meaning: an image, a horizontal rule.
    return node.type !== 'paragraph'
}

/**
 * An "empty" doc is still an object, so `!body` is always false. Every renderer and editor
 * emptiness check must go through this.
 */
export const isRichTextEmpty = (doc: RichTextDoc | null | undefined): boolean =>
    !richTextNodes(doc).some(hasVisibleContent)

const walkNodes = (nodes: RichTextNode[], visit: (node: RichTextNode) => void): void => {
    for (const node of nodes) {
        visit(node)
        if (node.content) walkNodes(node.content, visit)
    }
}

/** Every href carried by a link mark anywhere in the doc, deduped, in document order. */
export const richTextLinkHrefs = (doc: RichTextDoc | null | undefined): string[] => {
    const hrefs = new Set<string>()
    walkNodes(richTextNodes(doc), (node) => {
        for (const mark of node.marks ?? []) {
            if (mark.type !== 'link') continue
            const href = mark.attrs?.['href']
            if (typeof href === 'string' && href !== '') hrefs.add(href)
        }
    })
    return [...hrefs]
}

/**
 * Every asset id carried by an image node or an `asset:` link mark. Without this the delete guards
 * cannot see references buried in prose, and deleting a linked document silently breaks the link.
 */
export const richTextImageAssetIds = (doc: RichTextDoc | null | undefined): string[] => {
    const ids = new Set<string>()
    walkNodes(richTextNodes(doc), (node) => {
        if (node.type === 'image') {
            const assetId = node.attrs?.['assetId']
            if (typeof assetId === 'string' && assetId !== '') ids.add(assetId)
        }
        for (const mark of node.marks ?? []) {
            if (mark.type !== 'link') continue
            const href = mark.attrs?.['href']
            if (typeof href !== 'string') continue
            const assetId = getAssetId(href)
            if (assetId !== undefined && assetId !== '') ids.add(assetId)
        }
    })
    return [...ids]
}

const BLOCK_SEPARATOR = '\n\n'

/** Flatten to plain text: blocks separated by blank lines, hard breaks by a newline. */
export const richTextToPlainText = (doc: RichTextDoc | null | undefined): string => {
    const renderNode = (node: RichTextNode): string => {
        if (node.type === 'text') return node.text ?? ''
        if (node.type === 'hardBreak') return '\n'
        const inner = (node.content ?? []).map(renderNode).join('')
        return node.type === 'paragraph' || node.type === 'listItem' || node.type === 'heading'
            ? `${inner}${BLOCK_SEPARATOR}`
            : inner
    }

    return richTextNodes(doc).map(renderNode).join('').trim()
}

export const plainTextToRichText = (text: string): RichTextDoc => {
    const paragraphs = text
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== '')

    // Tiptap refuses a doc with no nodes, so empty input becomes one empty paragraph.
    if (paragraphs.length === 0) return emptyRichTextDoc()

    return {
        type: 'doc',
        content: paragraphs.map((paragraph) => ({
            type: 'paragraph',
            content: [{ type: 'text', text: paragraph }],
        })),
    }
}
